import { BookOpen } from "lucide-react";
import { NavLink } from "react-router-dom";

import { MethodChip } from "@components/MethodChip.tsx";
import { cn } from "@lib/cn.ts";
import { getBundle, guideHref, operationHref } from "@lib/content.ts";
import { guidesFor } from "@lib/guides.ts";

interface Props {
  version: string;
  onNavigate?: () => void;
}

const linkClass = ({ isActive }: { isActive: boolean }): string =>
  cn(
    "flex items-start gap-2 rounded-sm2 px-2 py-1.5 text-[13px] leading-snug transition",
    isActive
      ? "bg-accent-tint font-medium text-accent"
      : "text-muted hover:bg-surface-2 hover:text-ink",
  );

export function SidebarNav({ version, onNavigate }: Props) {
  const { nav } = getBundle(version);
  const guides = guidesFor(version);

  return (
    <nav aria-label="Documentation" className="pb-16">
      {guides.length > 0 ? (
        <section className="mb-5">
          <h2 className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-subtle">
            Guides
          </h2>
          <ul>
            {guides.map((guide) => (
              <li key={guide.slug}>
                <NavLink
                  to={guideHref(version, guide.slug)}
                  className={linkClass}
                  onClick={onNavigate}
                >
                  <BookOpen
                    className="mt-0.5 size-3.5 shrink-0 opacity-70"
                    aria-hidden
                  />
                  <span>{guide.title}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {nav.map((service) => (
        <section key={service.id} className="mb-5">
          <h2 className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-subtle">
            {service.title}
          </h2>

          {service.groups.map((group) => (
            <div key={group.tag} className="mb-2">
              {service.groups.length > 1 ? (
                <h3 className="px-2 py-1 text-[11.5px] font-medium text-muted/80">
                  {group.tag}
                </h3>
              ) : null}
              <ul>
                {group.operations.map((operation) => (
                  <li key={operation.id}>
                    <NavLink
                      to={operationHref(version, operation.id)}
                      className={linkClass}
                      onClick={onNavigate}
                    >
                      <MethodChip
                        method={operation.isWebhook ? "POST" : operation.method}
                        className="mt-px"
                      />
                      <span
                        className={cn(
                          "min-w-0 break-words",
                          operation.deprecated && "line-through opacity-60",
                        )}
                      >
                        {operation.summary || operation.path}
                      </span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>
      ))}
    </nav>
  );
}
